import React from 'react';

interface TypographyProps {
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2' | 'caption' | 'overline' | 'subtitle1' | 'subtitle2';
  component?: keyof JSX.IntrinsicElements;
  align?: 'left' | 'center' | 'right' | 'justify';
  color?: string;
  fontSize?: number | string;
  fontWeight?: number | string;
  className?: string;
  children?: React.ReactNode;
  sx?: React.CSSProperties;
  py?: number;
  px?: number;
  p?: number;
  mt?: number;
  mb?: number;
  ml?: number;
  mr?: number;
}

const Typography: React.FC<TypographyProps> = ({
  variant = 'body1',
  component,
  align,
  color,
  fontSize,
  fontWeight,
  className = '',
  children,
  sx,
  py,
  px,
  p,
  mt,
  mb,
  ml,
  mr,
}) => {
  const variantClasses = {
    h1: 'text-4xl font-bold',
    h2: 'text-3xl font-bold',
    h3: 'text-2xl font-semibold',
    h4: 'text-xl font-semibold',
    h5: 'text-lg font-medium',
    h6: 'text-base font-medium',
    body1: 'text-base',
    body2: 'text-sm',
    caption: 'text-xs',
    overline: 'text-xs uppercase tracking-wider',
    subtitle1: 'text-base font-medium',
    subtitle2: 'text-sm font-medium',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
  };

  const spacingClasses = [
    py !== undefined ? `py-${py}` : '',
    px !== undefined ? `px-${px}` : '',
    p !== undefined ? `p-${p}` : '',
    mt !== undefined ? `mt-${mt}` : '',
    mb !== undefined ? `mb-${mb}` : '',
    ml !== undefined ? `ml-${ml}` : '',
    mr !== undefined ? `mr-${mr}` : '',
  ].filter(Boolean).join(' ');

  const classes = `${variantClasses[variant]} ${align ? alignClasses[align] : ''} ${spacingClasses} ${className}`;

  const style: React.CSSProperties = {
    ...(color && { color }),
    ...(fontSize && { fontSize: typeof fontSize === 'number' ? `${fontSize}px` : fontSize }),
    ...(fontWeight && { fontWeight }),
    ...(py !== undefined && { paddingTop: `${py * 0.25}rem`, paddingBottom: `${py * 0.25}rem` }),
    ...(px !== undefined && { paddingLeft: `${px * 0.25}rem`, paddingRight: `${px * 0.25}rem` }),
    ...(p !== undefined && { padding: `${p * 0.25}rem` }),
    ...(mt !== undefined && { marginTop: `${mt * 0.25}rem` }),
    ...(mb !== undefined && { marginBottom: `${mb * 0.25}rem` }),
    ...(ml !== undefined && { marginLeft: `${ml * 0.25}rem` }),
    ...(mr !== undefined && { marginRight: `${mr * 0.25}rem` }),
    ...sx,
  };

  const Component = component || (variant.startsWith('h') ? (variant as keyof JSX.IntrinsicElements) : 'p');

  return (
    <Component className={classes} style={style}>
      {children}
    </Component>
  );
};

export default Typography;

